import { isRpaLoginTenant } from '../screens/RpaLoginScreen/rpaLoginConfig';
import { fetchForm } from '../service/form';
import type { FetchFormResponse } from '../service/types';
import { setSelectedFormIdInSession } from '../utils/form-id';

export const getSessionRoleStorageKey = (tenant?: string | null) =>
  `activeSessionRole:${String(tenant || '').toLowerCase()}`;

export const normalizeSessionRoles = (response: FetchFormResponse): string => {
  const roleSet = new Set<string>();
  if (Array.isArray(response?.sessionRoles)) {
    response.sessionRoles.forEach((role) => {
      const normalized = String(role || '').trim().toLowerCase();
      if (normalized) roleSet.add(normalized);
    });
  }
  const responseRole = String(response?.role || '').trim().toLowerCase();
  if (responseRole) roleSet.add(responseRole);
  if (Array.isArray(response?.forms)) {
    response.forms.forEach((form) => {
      const normalized = String(form?.role || '').trim().toLowerCase();
      if (normalized) roleSet.add(normalized);
    });
  }
  return Array.from(roleSet).sort().join('|');
};

export type ResolvePostLoginTargetOptions = {
  tenant: string;
  /** The page the user was on when sign-in started (carried in the OIDC state). */
  returnTo?: string | null;
  fetchJWT: (options?: { redirectOnFail?: boolean }) => Promise<{ idToken?: string }>;
  fetchFormFn?: typeof fetchForm;
};

const isTenantRoot = (tenant: string, path: string): boolean => {
  const [pathname] = path.split('?');
  return pathname.replace(/\/+$/, '') === `/${tenant}`;
};

/**
 * Where to send the user once they are signed in.
 *
 * A deep link under the tenant (anything other than the tenant root) wins.
 * Otherwise the pre-COLA rules apply: rpalogin lands on its landing page,
 * everyone else lands on My Forms unless they have no forms yet, in which
 * case the landing/new-form flow is used. The My Forms call also refreshes
 * the session roles and selected form id in sessionStorage, exactly as the
 * old sign-in screen did.
 */
export const resolvePostLoginTarget = async ({
  tenant,
  returnTo,
  fetchJWT,
  fetchFormFn = fetchForm,
}: ResolvePostLoginTargetOptions): Promise<string> => {
  const landingPath = `/${tenant}/`;
  const myFormsPath = `/${tenant}?my_forms=1&admin=0`;
  const requested = typeof returnTo === 'string' ? returnTo.trim() : '';
  const deepLink =
    requested.startsWith(`/${tenant}/`) || requested.startsWith(`/${tenant}?`)
      ? isTenantRoot(tenant, requested)
        ? ''
        : requested
      : '';

  const roleStorageKey = getSessionRoleStorageKey(tenant);
  setSelectedFormIdInSession(tenant, undefined);
  window.sessionStorage.removeItem(roleStorageKey);

  if (tenant === 'homebrew') {
    return deepLink || '/homebrew';
  }

  if (isRpaLoginTenant(tenant)) {
    return deepLink || landingPath;
  }

  let target = myFormsPath;
  try {
    const { idToken } = await fetchJWT({ redirectOnFail: false });
    if (idToken) {
      const response = await fetchFormFn(token(idToken), { my_forms: 'true' }, { 'X-Tenant': tenant });
      const normalizedSessionRoleSet = normalizeSessionRoles(response as FetchFormResponse);
      if (normalizedSessionRoleSet) {
        window.sessionStorage.setItem(roleStorageKey, normalizedSessionRoleSet);
      }
      const forms = Array.isArray(response?.forms) ? response.forms : [];

      // Routing rule: if user has any form access records (any role), stay on My Forms.
      if (forms.length > 0) {
        setSelectedFormIdInSession(tenant, undefined);
      } else {
        // Zero forms: always route to landing/new-form flow.
        const createdFormId = String(
          (response as { formId?: string; form_id?: string })?.formId ||
            (response as { formId?: string; form_id?: string })?.form_id ||
            '',
        ).trim();
        setSelectedFormIdInSession(tenant, createdFormId || undefined);
        target = landingPath;
      }
    }
  } catch (_error) {
    setSelectedFormIdInSession(tenant, undefined);
    window.sessionStorage.removeItem(roleStorageKey);
  }

  return deepLink || target;
};

const token = (value: string) => value;
